import React, { useState } from 'react'
import { opcionesDeporte } from '../../../../lib/deportesOrg'
import { useDeportes } from '../../../../hooks/useDeportes'
import { Package, AlertTriangle, Check } from 'lucide-react'
import {
  Button, Card, CardContent, Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../ui'
import { buildTableroCards, globalAvance, jugadorEnGrupo, playerDeportes } from './entregaHelpers'
import { ETAPA_LABEL, agruparPorGrupo } from './uniformesControlHelpers'

const ORIGEN_LABEL = {
  plan: { label: 'Del plan' },
  extra: { label: 'Solo extras' },
  mixto: { label: 'Plan + extras' },
}

const TableroAvance = ({ matriz, grupos, orgDeportes, onRegistrarPrenda, entregasDelPadron }) => {
  const { label: deporteLabel } = useDeportes()
  const [deporteFilter, setDeporteFilter] = useState('todos')
  const [grupoFilter, setGrupoFilter] = useState('todos')

  const jugadorById = Object.fromEntries((matriz.jugadores || []).map((j) => [j.id, j]))
  const prendaById = Object.fromEntries((matriz.prendas || []).map((p) => [p.id, p]))

  const deporteOptions = [
    { value: 'todos', label: 'Todos' },
    ...opcionesDeporte(orgDeportes),
  ]

  // Del padron: si el Tablero contara entregas de quien ya no participa, sus
  // totales no cuadrarian con las filas de la Lista.
  const entregasFiltradas = (entregasDelPadron || matriz.entregas || []).filter((e) => {
    const jugador = jugadorById[e.jugador_id]
    const prenda = prendaById[e.prenda_id]
    if (!jugador || !prenda) return false
    if (grupoFilter !== 'todos' && !jugadorEnGrupo(jugador, grupoFilter)) return false
    if (deporteFilter !== 'todos' && prenda.deporte !== 'todos' && !playerDeportes(jugador).includes(deporteFilter)) return false
    return true
  })

  const kpi = globalAvance(entregasFiltradas)
  const cards = buildTableroCards(entregasFiltradas, matriz.prendas || [])

  // Conteo por etapa (pedido/recibido/etc): suma de los subtotales por grupo,
  // que ya vienen calculados con etapaUniforme sobre cada entrega.
  const etapas = agruparPorGrupo(entregasFiltradas).reduce((acc, g) => ({
    porPedir: acc.porPedir + g.porPedir,
    enPedido: acc.enPedido + g.enPedido,
    pedido: acc.pedido + g.pedido,
    recibido: acc.recibido + g.recibido,
    entregado: acc.entregado + g.entregado,
  }), { porPedir: 0, enPedido: 0, pedido: 0, recibido: 0, entregado: 0 })

  // El tablero es una lista de trabajo, no un catalogo: primero lo que mas falta,
  // y a igualdad de pendientes primero lo que tiene mas gente sin talla (el bloqueo).
  // Las prendas ya completas se van al final.
  const cardsOrdenadas = [...cards].sort((a, b) =>
    b.pendientes - a.pendientes ||
    b.sinTalla - a.sinTalla ||
    (a.prenda.nombre || '').localeCompare(b.prenda.nombre || '')
  )

  return (
    <div>
      {/* Sin "Volver": Entregas, Tablero y Tallas son tres entradas del menu, no un
          flujo. Al entrar desde el menu no habia ningun lugar al que regresar. */}
      <div className="mb-4 grid grid-cols-2 gap-2">
        <Select value={deporteFilter} onValueChange={setDeporteFilter}>
          <SelectTrigger className="h-10" aria-label="Deporte"><SelectValue placeholder="Deporte" /></SelectTrigger>
          <SelectContent className="max-w-[calc(100vw-2rem)]">
            {deporteOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={grupoFilter} onValueChange={setGrupoFilter}>
          <SelectTrigger className="h-10" aria-label="Grupo"><SelectValue placeholder="Grupo" /></SelectTrigger>
          <SelectContent className="max-w-[calc(100vw-2rem)]">
            <SelectItem value="todos">Todos los grupos</SelectItem>
            {grupos.map((g) => <SelectItem key={g.id} value={g.id}>{g.nombre_del_grupo}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Un solo bloque de avance. Los 4 KPI anteriores eran el mismo dato dicho
          cuatro veces: total = entregadas + pendientes, y % = entregadas/total. */}
      <Card className="mb-4">
        <CardContent className="p-4 space-y-2">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-sm font-medium">Avance de entregas</span>
            <span className="text-2xl font-bold tabular-nums">{kpi.porcentaje}%</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
            <div className="h-full bg-green-500 transition-all" style={{ width: `${kpi.porcentaje}%` }} />
          </div>
          <p className="text-xs text-muted-foreground tabular-nums">
            {kpi.entregados} de {kpi.total} entregadas · {kpi.pendientes} pendientes
          </p>
          {/* Desglose por etapa del pedido: donde esta atorado lo que aun no se entrega. */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span className="tabular-nums">{ETAPA_LABEL.por_pedir}: <span className="font-medium text-foreground">{etapas.porPedir}</span></span>
            <span className="tabular-nums">{ETAPA_LABEL.en_pedido}: <span className="font-medium text-foreground">{etapas.enPedido}</span></span>
            <span className="tabular-nums">{ETAPA_LABEL.pedido}: <span className="font-medium text-foreground">{etapas.pedido}</span></span>
            <span className="tabular-nums">{ETAPA_LABEL.recibido}: <span className="font-medium text-foreground">{etapas.recibido}</span></span>
            <span className="tabular-nums">{ETAPA_LABEL.entregado}: <span className="font-medium text-foreground">{etapas.entregado}</span></span>
          </div>
          {/* El bloqueo real: sin talla no se puede entregar, y antes vivia en
              letra chica al pie de cada tarjeta. */}
          {kpi.sinTalla > 0 && (
            <p className="flex items-center gap-1.5 text-xs font-medium text-amber-700">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              {kpi.sinTalla} sin talla — captúrala para poder entregar
            </p>
          )}
        </CardContent>
      </Card>

      {cards.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Package className="mx-auto mb-3 h-12 w-12 opacity-30" />
            <p>No hay prendas por entregar con estos filtros.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {cardsOrdenadas.map((c) => {
            const pct = c.total > 0 ? Math.round((c.entregados / c.total) * 100) : 0
            const ol = ORIGEN_LABEL[c.origen]
            const completa = c.pendientes === 0
            return (
              // Las completas se atenuan: ya no piden trabajo, pero siguen visibles
              <Card key={c.prenda.id} className={completa ? 'opacity-60' : ''}>
                <CardContent className="space-y-2 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{c.prenda.nombre}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {deporteLabel(c.prenda.deporte)} · {ol.label}
                      </p>
                    </div>
                    <span className="shrink-0 text-sm font-semibold tabular-nums">{pct}%</span>
                  </div>

                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className={`h-full transition-all ${completa ? 'bg-green-500' : 'bg-primary'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>

                  {completa ? (
                    <p className="flex items-center gap-1.5 text-xs text-green-700">
                      <Check className="h-3.5 w-3.5 shrink-0" />
                      Todo entregado ({c.total})
                    </p>
                  ) : (
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs text-muted-foreground tabular-nums">
                        Faltan <span className="font-medium text-foreground">{c.pendientes}</span> de {c.total}
                        {c.sinTalla > 0 && (
                          <span className="ml-1.5 font-medium text-amber-700">· {c.sinTalla} sin talla</span>
                        )}
                      </p>
                      <Button size="sm" className="h-8 shrink-0" onClick={() => onRegistrarPrenda(c.prenda)}>
                        Registrar
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default TableroAvance
